/**
 * Montecapra motion core.
 *
 * Loaded only on the homepage (see layout/theme.liquid) after GSAP, ScrollTrigger and SplitText.
 * Scenes are declared in markup with `data-mc-scene="name other-name"` and registered here with
 * `Montecapra.motion.register(name, factory)`. A factory receives `(element, context)` and may return
 * a cleanup function. Animations created asynchronously must be wrapped in `context.gsapContext.add()`. Everything created inside a factory is scoped to its Shopify section, so the
 * theme editor can reload or remove a section without leaking ScrollTriggers.
 *
 * Content must always be visible without this script: initial hidden states are set by scenes
 * (gsap.set / gsap.from), never by CSS alone.
 */
(() => {
  const root = document.documentElement;

  const ACTIVE_CLASS = 'mc-motion';
  const READY_CLASS = 'mc-motion-ready';
  const SCENE_SELECTOR = '[data-mc-scene]';
  const SECTION_SELECTOR = '.shopify-section';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  const CONDITIONS = {
    isDesktop: '(min-width: 990px) and (prefers-reduced-motion: no-preference)',
    isMobile: '(max-width: 989px) and (prefers-reduced-motion: no-preference)',
    reduceMotion: REDUCED_MOTION_QUERY,
  };

  const scenes = new Map();
  const sections = new Map();

  let started = false;
  let refreshFrame = null;

  window.Montecapra = window.Montecapra || {};

  const api = {
    enabled: false,
    register,
    refresh: scheduleRefresh,
    initSection,
    destroySection,
    destroy,
  };

  window.Montecapra.motion = api;

  function register(name, factory) {
    if (typeof name !== 'string' || typeof factory !== 'function') return;
    scenes.set(name, factory);
  }

  function isDesignMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  function readDuration(property, fallback) {
    const value = getComputedStyle(root).getPropertyValue(property).trim();
    if (!value) return fallback;

    const number = parseFloat(value);
    if (Number.isNaN(number)) return fallback;

    return value.endsWith('ms') ? number / 1000 : number;
  }

  function getSectionKey(section) {
    return section.id || section;
  }

  function getSceneNames(element) {
    return (element.dataset.mcScene || '').split(/\s+/).filter(Boolean);
  }

  function runScenes(section, mediaContext) {
    const cleanups = [];
    const context = {
      section,
      conditions: mediaContext.conditions,
      gsapContext: mediaContext,
      designMode: isDesignMode(),
    };

    section.querySelectorAll(SCENE_SELECTOR).forEach((element) => {
      getSceneNames(element).forEach((name) => {
        const factory = scenes.get(name);
        if (!factory) return;

        try {
          const cleanup = factory(element, context);
          if (typeof cleanup === 'function') cleanups.push(cleanup);
        } catch (error) {
          console.error(`[Montecapra motion] Scene "${name}" failed`, error);
          window.gsap.set(element, { clearProps: 'all' });
        }
      });
    });

    return () => cleanups.forEach((cleanup) => cleanup());
  }

  function initSection(section) {
    if (!started || !section) return;

    destroySection(section);

    if (!section.querySelector(SCENE_SELECTOR)) return;

    const media = window.gsap.matchMedia();

    media.add(CONDITIONS, (mediaContext) => {
      if (mediaContext.conditions.reduceMotion) return;
      return runScenes(section, mediaContext);
    });

    sections.set(getSectionKey(section), media);
  }

  function destroySection(section) {
    if (!section) return;

    const key = getSectionKey(section);
    const media = sections.get(key);
    if (!media) return;

    media.revert();
    sections.delete(key);
  }

  function initAll() {
    const targets = new Set();

    document.querySelectorAll(SCENE_SELECTOR).forEach((element) => {
      targets.add(element.closest(SECTION_SELECTOR) || document.body);
    });

    targets.forEach(initSection);
  }

  function destroy() {
    sections.forEach((media) => media.revert());
    sections.clear();
  }

  function scheduleRefresh() {
    if (!started) return;
    if (refreshFrame) cancelAnimationFrame(refreshFrame);

    refreshFrame = requestAnimationFrame(() => {
      refreshFrame = null;
      window.ScrollTrigger.refresh();
    });
  }

  function onSectionLoad(event) {
    initSection(event.target);
    scheduleRefresh();
  }

  function onSectionUnload(event) {
    destroySection(event.target);
    scheduleRefresh();
  }

  function onReducedMotionChange(event) {
    root.classList.toggle(ACTIVE_CLASS, !event.matches);
    scheduleRefresh();
  }

  function bindEvents() {
    document.addEventListener('shopify:section:load', onSectionLoad);
    document.addEventListener('shopify:section:unload', onSectionUnload);
    document.addEventListener('shopify:section:reorder', scheduleRefresh);

    window.matchMedia(REDUCED_MOTION_QUERY).addEventListener('change', onReducedMotionChange);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(scheduleRefresh);
    }

    if (document.readyState === 'complete') {
      scheduleRefresh();
    } else {
      window.addEventListener('load', scheduleRefresh, { once: true });
    }
  }

  function start() {
    if (!root.classList.contains(ACTIVE_CLASS)) return;

    const { gsap, ScrollTrigger, SplitText } = window;

    if (!gsap || !ScrollTrigger) {
      root.classList.remove(ACTIVE_CLASS);
      return;
    }

    gsap.registerPlugin(ScrollTrigger);
    if (SplitText) gsap.registerPlugin(SplitText);

    gsap.defaults({
      duration: readDuration('--mc-duration-base', 0.9),
      ease: 'power3.out',
    });

    ScrollTrigger.config({ ignoreMobileResize: true });

    started = true;
    api.enabled = true;

    bindEvents();
    initAll();

    root.classList.add(READY_CLASS);
  }

  /*
   * Hero intro: media is never hidden or transformed (it is the LCP and CSS owns its Ken Burns).
   * The sequence starts once the first media is painted and fonts are ready, capped by
   * HERO_WAIT_LIMIT so the copy is never held back by a slow video or font.
   */
  const HERO_WAIT_LIMIT = 1500;
  const HERO_INTRO = {
    desktop: {
      duration: 0.85,
      eyebrowY: 16,
      titleYPercent: 110,
      titleDuration: 1.15,
      titleStagger: 0.12,
      copyY: 20,
      ctaY: 14,
      at: { eyebrow: 0.1, title: 0.3, copy: 0.85, cta: 1.1 },
    },
    mobile: {
      duration: 0.7,
      eyebrowY: 10,
      titleYPercent: 105,
      titleDuration: 1,
      titleStagger: 0.07,
      copyY: 12,
      ctaY: 8,
      at: { eyebrow: 0.05, title: 0.25, copy: 0.7, cta: 0.9 },
    },
  };

  function whenMediaPainted(media) {
    if (!media) return Promise.resolve();

    const video = media.querySelector('video');
    if (video) {
      if (video.readyState >= 2) return Promise.resolve();
      return new Promise((resolve) => {
        video.addEventListener('loadeddata', resolve, { once: true });
        video.addEventListener('error', resolve, { once: true });
      });
    }

    const image = media.querySelector('img');
    if (!image || image.complete) return Promise.resolve();
    return image.decode().catch(() => {});
  }

  register('hero-intro', (scene, { gsapContext, conditions }) => {
    // Plays once per render: crossing a breakpoint re-runs matchMedia but must not replay the intro.
    if (scene.dataset.mcIntroPlayed === 'true') return;

    const mode = scene.dataset.contentAnimation || 'fade-up';
    if (mode === 'none') return;

    const { gsap, SplitText } = window;
    const media = scene.querySelector('[data-mc-hero-media]');
    const eyebrow = scene.querySelector('[data-mc-hero-eyebrow]');
    const title = scene.querySelector('[data-mc-hero-title]');
    const copy = scene.querySelector('[data-mc-hero-copy]');
    const cta = scene.querySelector('[data-mc-hero-cta]');
    const items = [eyebrow, title, copy, cta].filter(Boolean);
    if (!items.length) return;

    const settings = conditions.isDesktop ? HERO_INTRO.desktop : HERO_INTRO.mobile;
    const rise = mode === 'fade-up';

    let split = null;
    let cancelled = false;
    let waitTimer = null;

    const releaseSplit = () => {
      if (!split) return;
      split.revert();
      split = null;
    };

    const reveal = () => {
      releaseSplit();
      gsap.set(items, { clearProps: 'transform,opacity,visibility' });
    };

    const enter = (timeline, target, y, at, duration) => {
      const to = { autoAlpha: 1, y: 0 };
      // An explicit `duration: undefined` would override the timeline default with 0.
      if (duration) to.duration = duration;
      timeline.fromTo(target, { autoAlpha: 0, y: rise ? y : 0 }, to, at);
    };

    const play = () => {
      if (cancelled) return;

      try {
        gsapContext.add(() => {
          const timeline = gsap.timeline({
            defaults: { duration: settings.duration, ease: 'power3.out' },
            onComplete: () => {
              scene.dataset.mcIntroPlayed = 'true';
              reveal();
            },
          });

          if (eyebrow) enter(timeline, eyebrow, settings.eyebrowY, settings.at.eyebrow);

          if (title) {
            if (rise && SplitText) {
              split = SplitText.create(title, {
                type: 'lines',
                mask: 'lines',
                linesClass: 'mc-split-line',
                aria: 'auto',
              });
              gsap.set(title, { autoAlpha: 1 });
              timeline.from(
                split.lines,
                {
                  yPercent: settings.titleYPercent,
                  duration: settings.titleDuration,
                  stagger: settings.titleStagger,
                  ease: 'power4.out',
                },
                settings.at.title
              );
            } else {
              enter(timeline, title, settings.copyY, settings.at.title, settings.titleDuration);
            }
          }

          if (copy) enter(timeline, copy, settings.copyY, title ? settings.at.copy : settings.at.title);
          if (cta) enter(timeline, cta, settings.ctaY, copy ? settings.at.cta : settings.at.copy);
        });
      } catch (error) {
        console.error('[Montecapra motion] Scene "hero-intro" failed to play', error);
        reveal();
      }
    };

    // Hidden synchronously, before .mc-motion-ready lifts [data-mc-cloak]; reverted by gsapContext.
    gsap.set(items, { autoAlpha: 0 });

    const fontsReady = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
    const waitLimit = new Promise((resolve) => {
      waitTimer = setTimeout(resolve, HERO_WAIT_LIMIT);
    });
    Promise.race([Promise.all([fontsReady, whenMediaPainted(media)]), waitLimit]).then(play, play);

    return () => {
      cancelled = true;
      clearTimeout(waitTimer);
      releaseSplit();
    };
  });

  /*
   * Mountain: first scroll-driven chapter. No pin: one photograph and a short headline read well
   * with a scrubbed drift, and pinning would add a full viewport of dead scroll on every device.
   * Desktop scrubs the media (scale ≤ 1.06 with a smaller drift, so the overhang always covers the
   * frame), thins the hero mist and deepens the tint. The copy reveals once; mobile only fades it in.
   */
  const MOUNTAIN_FONT_LIMIT = 1200;
  const MOUNTAIN = {
    desktop: {
      scrub: 0.6,
      scale: 1.06,
      driftYPercent: 2,
      overlayFrom: 0.7,
      fogTo: 0.45,
      revealStart: 'top 82%',
      duration: 0.9,
      itemY: 18,
      titleYPercent: 110,
      titleDuration: 1.1,
      titleStagger: 0.1,
      at: { eyebrow: 0, title: 0.15, altitude: 0.6, copy: 0.75 },
    },
    mobile: {
      revealStart: 'top 92%',
      duration: 0.6,
      itemY: 12,
      stagger: 0.08,
    },
  };

  register('mountain', (scene, { gsapContext, conditions }) => {
    const { gsap, ScrollTrigger, SplitText } = window;
    const media = scene.querySelector('[data-mc-mountain-media]');
    const overlay = scene.querySelector('[data-mc-mountain-overlay]');
    const fog = scene.querySelector('[data-mc-mountain-fog]');
    const content = scene.querySelector('[data-mc-mountain-content]');
    const eyebrow = scene.querySelector('[data-mc-mountain-eyebrow]');
    const title = scene.querySelector('[data-mc-mountain-title]');
    const altitude = scene.querySelector('[data-mc-mountain-altitude]');
    const rule = scene.querySelector('.mc-mountain__altitude-rule');
    const copy = scene.querySelector('[data-mc-mountain-copy]');
    const items = [eyebrow, title, altitude, copy].filter(Boolean);
    const isDesktop = Boolean(conditions.isDesktop);

    if (isDesktop) {
      const scrollTrigger = {
        trigger: scene,
        start: 'top bottom',
        end: 'bottom top',
        scrub: MOUNTAIN.desktop.scrub,
      };

      if (media) {
        gsap.fromTo(
          media,
          { scale: 1, yPercent: 0 },
          { scale: MOUNTAIN.desktop.scale, yPercent: MOUNTAIN.desktop.driftYPercent, ease: 'none', scrollTrigger }
        );
      }

      if (overlay) {
        gsap.fromTo(
          overlay,
          { opacity: MOUNTAIN.desktop.overlayFrom },
          { opacity: 1, ease: 'none', scrollTrigger: { ...scrollTrigger, end: 'top top' } }
        );
      }

      if (fog) {
        gsap.fromTo(
          fog,
          { opacity: 1 },
          { opacity: MOUNTAIN.desktop.fogTo, ease: 'none', scrollTrigger: { ...scrollTrigger, end: 'top top' } }
        );
      }
    }

    // The copy reveals once per render; crossing a breakpoint keeps it visible.
    if (!content || !items.length || scene.dataset.mcMountainRevealed === 'true') return;

    let split = null;
    let cancelled = false;
    let fontTimer = null;

    const releaseSplit = () => {
      if (!split) return;
      split.revert();
      split = null;
    };

    const reveal = () => {
      scene.dataset.mcMountainRevealed = 'true';
      releaseSplit();
      gsap.set(items, { clearProps: 'transform,opacity,visibility' });
      if (rule) gsap.set(rule, { clearProps: 'transform' });
    };

    const playDesktop = () => {
      const settings = MOUNTAIN.desktop;
      const timeline = gsap.timeline({
        defaults: { duration: settings.duration, ease: 'power3.out' },
        onComplete: reveal,
      });

      if (eyebrow) {
        timeline.fromTo(eyebrow, { autoAlpha: 0, y: settings.itemY }, { autoAlpha: 1, y: 0 }, settings.at.eyebrow);
      }

      if (title) {
        if (SplitText) {
          split = SplitText.create(title, {
            type: 'lines',
            mask: 'lines',
            linesClass: 'mc-split-line',
            aria: 'auto',
          });
          gsap.set(title, { autoAlpha: 1 });
          timeline.from(
            split.lines,
            {
              yPercent: settings.titleYPercent,
              duration: settings.titleDuration,
              stagger: settings.titleStagger,
              ease: 'power4.out',
            },
            settings.at.title
          );
        } else {
          timeline.fromTo(title, { autoAlpha: 0, y: settings.itemY }, { autoAlpha: 1, y: 0 }, settings.at.title);
        }
      }

      if (altitude) {
        timeline.fromTo(altitude, { autoAlpha: 0, y: settings.itemY }, { autoAlpha: 1, y: 0 }, settings.at.altitude);
        if (rule) timeline.from(rule, { scaleX: 0, duration: settings.titleDuration }, settings.at.altitude);
      }

      if (copy) {
        timeline.fromTo(copy, { autoAlpha: 0, y: settings.itemY }, { autoAlpha: 1, y: 0 }, settings.at.copy);
      }
    };

    const playMobile = () => {
      const settings = MOUNTAIN.mobile;
      gsap.fromTo(
        items,
        { autoAlpha: 0, y: settings.itemY },
        {
          autoAlpha: 1,
          y: 0,
          duration: settings.duration,
          stagger: settings.stagger,
          ease: 'power2.out',
          onComplete: reveal,
        }
      );
    };

    const arm = () => {
      if (cancelled) return;

      try {
        gsapContext.add(() => {
          ScrollTrigger.create({
            trigger: content,
            start: isDesktop ? MOUNTAIN.desktop.revealStart : MOUNTAIN.mobile.revealStart,
            once: true,
            onEnter: () => {
              try {
                gsapContext.add(isDesktop ? playDesktop : playMobile);
              } catch (error) {
                console.error('[Montecapra motion] Scene "mountain" failed to play', error);
                reveal();
              }
            },
          });
        });
      } catch (error) {
        console.error('[Montecapra motion] Scene "mountain" failed to arm', error);
        reveal();
      }
    };

    // Hidden synchronously, before .mc-motion-ready lifts [data-mc-cloak]; reverted by gsapContext.
    gsap.set(items, { autoAlpha: 0 });

    // SplitText measures lines, so the reveal is armed once fonts settle (capped so copy is never stuck).
    const fontsReady = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
    const fontLimit = new Promise((resolve) => {
      fontTimer = setTimeout(resolve, MOUNTAIN_FONT_LIMIT);
    });
    Promise.race([fontsReady, fontLimit]).then(arm, arm);

    return () => {
      cancelled = true;
      clearTimeout(fontTimer);
      releaseSplit();
    };
  });

  /*
   * Origin: the human chapter after the mountain, deliberately calmer. No pin and no shared timeline
   * with the mountain: each block (people, process, steps, closing) reveals once as it enters.
   * Desktop adds a clip reveal on the ambient photo, a scrubbed hairline across the steps, a split
   * closing title and a ≤2% drift on the closing photo (scaled so its overhang always covers the frame).
   * Mobile/tablet keep short fades and leave the step carousel untouched.
   * `.is-motion` on the section disables its IntersectionObserver reveal, which remains the no-GSAP fallback.
   */
  const ORIGIN_FONT_LIMIT = 1200;
  const ORIGIN = {
    desktop: {
      start: 'top 78%',
      duration: 0.9,
      itemY: 16,
      mediaY: 24,
      clipDuration: 1.3,
      entryScale: 1.08,
      detailScale: 1.03,
      insetY: 10,
      insetRotate: -2,
      stepY: 24,
      stepStagger: 0.12,
      titleYPercent: 110,
      titleDuration: 1.1,
      titleStagger: 0.14,
      progressScrub: 0.8,
      parallaxScrub: 0.6,
      parallaxYPercent: 2,
      parallaxScale: 1.06,
    },
    mobile: {
      start: 'top 88%',
      duration: 0.6,
      itemY: 10,
      mediaY: 12,
      stagger: 0.08,
    },
  };

  register('origin', (scene, { gsapContext, conditions }) => {
    const { gsap, ScrollTrigger, SplitText } = window;
    const isDesktop = Boolean(conditions.isDesktop);
    const settings = isDesktop ? ORIGIN.desktop : ORIGIN.mobile;
    const find = (name) => scene.querySelector(`[data-mc-origin-${name}]`);
    const childrenOf = (element) => (element ? Array.from(element.children) : []);
    const imageOf = (element) => (element ? element.querySelector('img') : null);

    const hero = find('hero');
    const content = find('content');
    const entry = find('entry');
    const portrait = find('portrait');
    const process = find('process');
    const intro = find('intro');
    const detail = find('detail');
    const detailMain = find('detail-main');
    const detailInset = find('detail-inset');
    const steps = find('steps');
    const closing = find('closing');
    const closingMedia = find('closing-media');
    const closingContent = find('closing-content');
    const closingTitle = find('closing-title');

    let split = null;
    let cancelled = false;
    let fontTimer = null;

    const releaseSplit = () => {
      if (!split) return;
      split.revert();
      split = null;
    };

    // Each block: `hide` runs synchronously, `build` fills a one-shot timeline, `targets` are cleared after it.
    const blocks = [];
    const addBlock = (key, trigger, block) => {
      if (!trigger || scene.dataset[key] === 'true') return;
      blocks.push({ key, trigger, ...block, targets: block.targets.filter(Boolean) });
    };

    const fadeUp = (timeline, targets, y, at, extra = {}) => {
      const list = [].concat(targets).filter(Boolean);
      if (!list.length) return;
      timeline.fromTo(list, { autoAlpha: 0, y }, { autoAlpha: 1, y: 0, ...extra }, at);
    };

    scene.classList.add('is-motion');

    // A — people: out of the landscape, down to human scale.
    const contentItems = childrenOf(content);
    if (isDesktop) {
      addBlock('mcOriginPeopleRevealed', hero, {
        targets: [portrait, ...contentItems],
        hide: () => gsap.set([portrait, ...contentItems].filter(Boolean), { autoAlpha: 0 }),
        build: (timeline) => {
          fadeUp(timeline, contentItems, settings.itemY, 0, { stagger: 0.08 });
          fadeUp(timeline, portrait, settings.mediaY, 0.15, { duration: 1 });
        },
      });

      // The ambient photo sits low in its column, so it has its own trigger to reveal where it is seen.
      const entryImage = imageOf(entry);
      addBlock('mcOriginEntryRevealed', entry, {
        start: 'top 88%',
        targets: [entry, entryImage],
        hide: () => {
          gsap.set(entry, { clipPath: 'inset(100% 0% 0% 0%)' });
          if (entryImage) gsap.set(entryImage, { scale: settings.entryScale });
        },
        build: (timeline) => {
          timeline.to(entry, { clipPath: 'inset(0% 0% 0% 0%)', duration: settings.clipDuration, ease: 'power3.inOut' }, 0);
          if (entryImage) timeline.to(entryImage, { scale: 1, duration: settings.clipDuration + 0.4 }, 0);
        },
      });
    } else {
      addBlock('mcOriginPeopleRevealed', hero, {
        targets: [entry, portrait, ...contentItems],
        hide: () => gsap.set([entry, portrait, ...contentItems].filter(Boolean), { autoAlpha: 0 }),
        build: (timeline) => {
          fadeUp(timeline, contentItems, settings.itemY, 0, { stagger: settings.stagger });
          fadeUp(timeline, [entry, portrait], settings.mediaY, 0.2, { stagger: settings.stagger });
        },
      });
    }

    // B — coffee and work: the process header and its detail pair.
    const introItems = childrenOf(intro);
    if (isDesktop) {
      const detailImage = imageOf(detailMain);
      addBlock('mcOriginProcessRevealed', process, {
        targets: [...introItems, detailMain, detailImage, detailInset],
        hide: () => {
          gsap.set([...introItems, detailMain, detailInset].filter(Boolean), { autoAlpha: 0 });
          if (detailImage) gsap.set(detailImage, { scale: settings.detailScale });
        },
        build: (timeline) => {
          fadeUp(timeline, introItems, settings.itemY, 0, { stagger: 0.08 });
          if (detailMain) timeline.to(detailMain, { autoAlpha: 1, duration: 1 }, 0.1);
          if (detailImage) timeline.to(detailImage, { scale: 1, duration: 1.6 }, 0.1);
          if (detailInset) {
            timeline.fromTo(
              detailInset,
              { autoAlpha: 0, y: settings.insetY, rotate: settings.insetRotate },
              { autoAlpha: 1, y: 0, rotate: 0, duration: 1 },
              0.45
            );
          }
        },
      });
    } else {
      addBlock('mcOriginProcessRevealed', process, {
        targets: [...introItems, detail],
        hide: () => gsap.set([...introItems, detail].filter(Boolean), { autoAlpha: 0 }),
        build: (timeline) => {
          fadeUp(timeline, introItems, settings.itemY, 0, { stagger: settings.stagger });
          fadeUp(timeline, detail, settings.mediaY, 0.15);
        },
      });
    }

    // Steps — desktop only: a discreet 01 → 04 stagger. The mobile carousel keeps its native scroll-snap.
    const stepItems = childrenOf(steps);
    if (isDesktop && stepItems.length) {
      addBlock('mcOriginStepsRevealed', steps, {
        targets: stepItems,
        hide: () => gsap.set(stepItems, { autoAlpha: 0 }),
        build: (timeline) => {
          fadeUp(timeline, stepItems, settings.stepY, 0, { duration: 0.8, stagger: settings.stepStagger });
        },
      });
    }

    // C — fruit → capsule → product: a narrative pause before the marquee.
    const closingItems = childrenOf(closingContent);
    const titleIndex = closingItems.indexOf(closingTitle);
    const beforeTitle = titleIndex > -1 ? closingItems.slice(0, titleIndex) : closingItems;
    const afterTitle = titleIndex > -1 ? closingItems.slice(titleIndex + 1) : [];
    addBlock('mcOriginClosingRevealed', closing, {
      targets: [closing, ...closingItems],
      hide: () => gsap.set([closing, ...closingItems].filter(Boolean), { autoAlpha: 0 }),
      build: (timeline) => {
        if (!isDesktop) {
          fadeUp(timeline, closing, settings.mediaY, 0);
          fadeUp(timeline, closingItems, settings.itemY, 0.15, { stagger: settings.stagger });
          return;
        }

        fadeUp(timeline, closing, settings.mediaY, 0, { duration: 1 });
        fadeUp(timeline, beforeTitle, settings.itemY, 0.3);

        if (closingTitle && SplitText) {
          split = SplitText.create(closingTitle, {
            type: 'lines',
            mask: 'lines',
            linesClass: 'mc-split-line',
            aria: 'auto',
          });
          gsap.set(closingTitle, { autoAlpha: 1 });
          timeline.from(
            split.lines,
            {
              yPercent: settings.titleYPercent,
              duration: settings.titleDuration,
              stagger: settings.titleStagger,
              ease: 'power4.out',
            },
            0.4
          );
        } else {
          fadeUp(timeline, closingTitle, settings.itemY, 0.4);
        }

        fadeUp(timeline, afterTitle, settings.itemY, 0.95, { stagger: 0.1 });
      },
    });

    // Scrubbed layers: desktop only, never pinned, and they never touch the marquee.
    if (isDesktop) {
      if (steps && stepItems.length > 1) {
        scene.classList.add('has-progress');
        gsap.fromTo(
          steps,
          { '--mo-progress': 0 },
          {
            '--mo-progress': 1,
            ease: 'none',
            scrollTrigger: { trigger: steps, start: 'top 85%', end: 'bottom 55%', scrub: settings.progressScrub },
          }
        );
      }

      const closingImage = imageOf(closingMedia);
      if (closingImage) {
        gsap.fromTo(
          closingImage,
          { yPercent: -settings.parallaxYPercent, scale: settings.parallaxScale },
          {
            yPercent: settings.parallaxYPercent,
            scale: settings.parallaxScale,
            ease: 'none',
            scrollTrigger: { trigger: closing, start: 'top bottom', end: 'bottom top', scrub: settings.parallaxScrub },
          }
        );
      }
    }

    const settle = (block) => {
      scene.dataset[block.key] = 'true';
      if (block.key === 'mcOriginClosingRevealed') releaseSplit();
      gsap.set(block.targets, { clearProps: 'opacity,visibility,transform,clipPath' });
    };

    const play = (block) => {
      try {
        gsapContext.add(() => {
          const timeline = gsap.timeline({
            defaults: { duration: settings.duration, ease: 'power3.out' },
            onComplete: () => settle(block),
          });
          block.build(timeline);
        });
      } catch (error) {
        console.error('[Montecapra motion] Scene "origin" failed to play', error);
        releaseSplit();
        settle(block);
      }
    };

    const arm = () => {
      if (cancelled) return;

      try {
        gsapContext.add(() => {
          blocks.forEach((block) => {
            ScrollTrigger.create({
              trigger: block.trigger,
              start: block.start || settings.start,
              once: true,
              onEnter: () => play(block),
            });
          });
        });
      } catch (error) {
        console.error('[Montecapra motion] Scene "origin" failed to arm', error);
        blocks.forEach(settle);
      }
    };

    // Hidden synchronously, in the same task that disabled the CSS reveal; reverted by gsapContext.
    blocks.forEach((block) => block.hide());

    // The closing title is split by lines, so reveals are armed once fonts settle (capped).
    const fontsReady = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
    const fontLimit = new Promise((resolve) => {
      fontTimer = setTimeout(resolve, ORIGIN_FONT_LIMIT);
    });
    Promise.race([fontsReady, fontLimit]).then(arm, arm);

    return () => {
      cancelled = true;
      clearTimeout(fontTimer);
      releaseSplit();
      scene.classList.remove('is-motion', 'has-progress');
    };
  });

  /*
   * Maquila (home teaser): a lighter epilogue. No pin and no split headline.
   * Desktop scrubs the photograph (scale ≤ 1.04 with a smaller drift, so the overhang always covers
   * the frame); mobile/tablet keep it still. Copy, claims and CTA reveal once, CTA last.
   */
  const MAQUILA = {
    desktop: {
      scrub: 0.6,
      scale: 1.04,
      driftYPercent: 1.5,
      start: 'top 75%',
      duration: 0.9,
      itemY: 18,
      itemStagger: 0.1,
      factStagger: 0.08,
      at: { items: 0, facts: 0.45, cta: 0.8 },
    },
    mobile: {
      start: 'top 85%',
      duration: 0.6,
      itemY: 10,
      itemStagger: 0.07,
      factStagger: 0.06,
      at: { items: 0, facts: 0.3, cta: 0.5 },
    },
  };

  register('maquila-home', (scene, { gsapContext, conditions }) => {
    const { gsap, ScrollTrigger } = window;
    const isDesktop = Boolean(conditions.isDesktop);
    const settings = isDesktop ? MAQUILA.desktop : MAQUILA.mobile;
    const media = scene.querySelector('[data-mc-maquila-media]');
    // The image moves inside its clipped frame, so the drift never leaks out of the stacked layout either.
    const image = media ? media.querySelector('img') : null;
    const items = Array.from(scene.querySelectorAll('[data-mc-maquila-item]'));
    const facts = Array.from(scene.querySelectorAll('[data-mc-maquila-fact]'));
    const cta = scene.querySelector('[data-mc-maquila-cta]');
    const targets = [...items, ...facts, cta].filter(Boolean);

    if (isDesktop && image) {
      gsap.fromTo(
        image,
        { scale: 1, yPercent: 0 },
        {
          scale: settings.scale,
          yPercent: settings.driftYPercent,
          ease: 'none',
          scrollTrigger: { trigger: scene, start: 'top bottom', end: 'bottom top', scrub: settings.scrub },
        }
      );
    }

    // The copy reveals once per render; crossing a breakpoint keeps it visible.
    if (!targets.length || scene.dataset.mcMaquilaRevealed === 'true') return;

    const reveal = () => {
      scene.dataset.mcMaquilaRevealed = 'true';
      gsap.set(targets, { clearProps: 'transform,opacity,visibility' });
    };

    const play = () => {
      try {
        gsapContext.add(() => {
          const timeline = gsap.timeline({
            defaults: { duration: settings.duration, ease: 'power3.out' },
            onComplete: reveal,
          });
          const fadeUp = (list, at, stagger) => {
            if (!list.length) return;
            timeline.fromTo(list, { autoAlpha: 0, y: settings.itemY }, { autoAlpha: 1, y: 0, stagger }, at);
          };

          fadeUp(items, settings.at.items, settings.itemStagger);
          fadeUp(facts, settings.at.facts, settings.factStagger);
          if (cta) fadeUp([cta], settings.at.cta, 0);
        });
      } catch (error) {
        console.error('[Montecapra motion] Scene "maquila-home" failed to play', error);
        reveal();
      }
    };

    // Hidden synchronously, before .mc-motion-ready lifts [data-mc-cloak]; reverted by gsapContext.
    gsap.set(targets, { autoAlpha: 0 });

    ScrollTrigger.create({
      trigger: scene.querySelector('.mc-maquila-cine__inner') || scene,
      start: settings.start,
      once: true,
      onEnter: play,
    });
  });

  /*
   * Ritual: the header and the steps reveal once, steps staggered in reading order.
   * Images are never hidden; desktop only adds a slight settle on each photograph.
   */
  const RITUAL = {
    desktop: { start: 'top 72%', duration: 0.9, itemY: 18, stepY: 32, stepStagger: 0.12, imageScale: 1.08 },
    mobile: { start: 'top 85%', duration: 0.6, itemY: 10, stepY: 16, stepStagger: 0.08, imageScale: 1 },
  };

  register('ritual', (scene, { gsapContext, conditions }) => {
    const { gsap, ScrollTrigger } = window;
    if (scene.dataset.mcRitualRevealed === 'true') return;

    const settings = conditions.isDesktop ? RITUAL.desktop : RITUAL.mobile;
    const items = Array.from(scene.querySelectorAll('[data-mc-ritual-item]'));
    const steps = Array.from(scene.querySelectorAll('[data-mc-ritual-step]'));
    const copies = steps.map((step) => step.querySelector('.mc-ritual__copy')).filter(Boolean);
    const images = steps.map((step) => step.querySelector('.mc-ritual__image')).filter(Boolean);
    const targets = [...items, ...copies];
    if (!targets.length) return;

    const reveal = () => {
      scene.dataset.mcRitualRevealed = 'true';
      gsap.set([...targets, ...images], { clearProps: 'transform,opacity,visibility' });
    };

    const play = () => {
      try {
        gsapContext.add(() => {
          const timeline = gsap.timeline({
            defaults: { duration: settings.duration, ease: 'power3.out' },
            onComplete: reveal,
          });
          if (items.length) {
            timeline.fromTo(items, { autoAlpha: 0, y: settings.itemY }, { autoAlpha: 1, y: 0, stagger: 0.1 }, 0);
          }
          if (copies.length) {
            timeline.fromTo(copies, { autoAlpha: 0, y: settings.stepY }, { autoAlpha: 1, y: 0, stagger: settings.stepStagger }, 0.2);
          }
          if (images.length && settings.imageScale > 1) {
            timeline.fromTo(
              images,
              { scale: settings.imageScale },
              { scale: 1, duration: 1.4, stagger: settings.stepStagger, ease: 'power2.out' },
              0.1
            );
          }
        });
      } catch (error) {
        console.error('[Montecapra motion] Scene "ritual" failed to play', error);
        reveal();
      }
    };

    // Hidden synchronously, before .mc-motion-ready lifts [data-mc-cloak]; reverted by gsapContext.
    gsap.set(targets, { autoAlpha: 0 });

    // Loading below the section (anchor or restored scroll) leaves instead of entering, so both play.
    ScrollTrigger.create({ trigger: scene, start: settings.start, once: true, onEnter: play, onLeave: play, onEnterBack: play });
  });

  start();
})();
